/**
 * DEV-ONLY Access Debug Panel
 *
 * Shows current user context and feature access checks.
 * This component ONLY renders in development mode.
 *
 * Usage:
 * <DevAccessDebugPanel isOpen={open} onClose={() => setOpen(false)} />
 */

import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useUserAccessInfo } from '../../hooks/useAccess';
import { FEATURE_POLICIES, canAccess } from '../../utils/access';
import {
  selectCentralUser,
  selectCurrentCentralWorkspace,
  selectIsCentralAuthenticated,
} from '../../store/slices/centralAuthSlice';

// ============================================================
// DEV-ONLY GUARD - This component returns null in production
// ============================================================

interface DevAccessDebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DevAccessDebugPanel({ isOpen, onClose }: DevAccessDebugPanelProps) {
  // DEV-ONLY: Return null in production builds
  if (!import.meta.env.DEV) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  return <DevAccessDebugPanelContent onClose={onClose} />;
}

// ============================================================
// Panel Content (only rendered in DEV mode)
// ============================================================

function DevAccessDebugPanelContent({ onClose }: { onClose: () => void }) {
  const [featureListExpanded, setFeatureListExpanded] = useState(false);

  // Auth state
  const centralUser = useSelector(selectCentralUser);
  const centralWorkspace = useSelector(selectCurrentCentralWorkspace);
  const isCentralAuth = useSelector(selectIsCentralAuthenticated);

  // Access info (reuses existing hooks)
  const userAccessInfo = useUserAccessInfo();

  // Determine auth source
  const authSource = useMemo(() => {
    if (isCentralAuth && centralUser) {
      return 'Central';
    }
    return 'None';
  }, [isCentralAuth, centralUser]);

  // Get all feature access results
  const featureAccessResults = useMemo(() => {
    return Object.keys(FEATURE_POLICIES).map((featureKey) => {
      const result = canAccess(featureKey, userAccessInfo);
      return {
        featureKey,
        allowed: result.allowed,
        reason: result.reason,
      };
    });
  }, [userAccessInfo]);

  // Quick links based on feature access
  const quickLinks = useMemo(() => {
    const links = [
      { path: '/practice', label: 'Practice', feature: 'practice:classic' },
      { path: '/marketplace', label: 'Marketplace', feature: 'marketplace:browse' },
      { path: '/coaching', label: 'Coaching', feature: 'coaching:as-client' },
    ];

    // Add org-dependent routes if user has orgId
    if (userAccessInfo?.organizationId) {
      links.push(
        // Membership is Central's (Settings → Members); /hiring is retired (410).
        { path: '/settings/members', label: 'Org Members', feature: 'org:members' },
        { path: '/training', label: 'Training Modules', feature: 'training:modules' }
      );
    }

    return links.map((link) => ({
      ...link,
      access: canAccess(link.feature, userAccessInfo),
    }));
  }, [userAccessInfo]);

  // Count allowed/denied
  const allowedCount = featureAccessResults.filter((r) => r.allowed).length;
  const deniedCount = featureAccessResults.filter((r) => !r.allowed).length;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-2">
            <span className="text-lg">🛠️</span>
            <h2 className="text-lg font-semibold text-amber-900">DEV Access Debug Panel</h2>
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
              DEV ONLY
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-amber-600 hover:text-amber-900 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Current User Context */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>👤</span> Current User Context
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div className="text-gray-500">Role:</div>
                <div className="font-medium">
                  <RoleBadge role={userAccessInfo?.role || 'none'} />
                </div>

                <div className="text-gray-500">Account Type:</div>
                <div className="font-medium">
                  <AccountTypeBadge type={userAccessInfo?.accountType || 'none'} />
                </div>

                <div className="text-gray-500">Plan Tier:</div>
                <div className="font-medium">{userAccessInfo?.planTier || 'none'}</div>

                <div className="text-gray-500">Workspace/OrgId:</div>
                <div className="font-medium text-xs break-all">
                  {centralWorkspace?.id || userAccessInfo?.organizationId || (
                    <span className="text-gray-400">none</span>
                  )}
                </div>

                <div className="text-gray-500">Auth Source:</div>
                <div className="font-medium">
                  <AuthSourceBadge source={authSource} />
                </div>

                <div className="text-gray-500">DEV_DISABLE_PLAN_LIMITS:</div>
                <div className="font-medium text-gray-400 text-xs">
                  (check backend logs)
                </div>
              </div>
            </div>
          </section>

          {/* Feature Access Checks */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <span>🔐</span> Feature Access Matrix
                <span className="text-xs font-normal text-gray-500">
                  ({allowedCount} allowed, {deniedCount} denied)
                </span>
              </h3>
              <button
                onClick={() => setFeatureListExpanded(!featureListExpanded)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {featureListExpanded ? 'Collapse' : 'Expand All'}
              </button>
            </div>

            <div
              className={`bg-gray-50 rounded-lg overflow-hidden transition-all ${
                featureListExpanded ? 'max-h-[400px]' : 'max-h-48'
              } overflow-y-auto`}
            >
              <table className="w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Feature</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {featureAccessResults.map(({ featureKey, allowed, reason }) => (
                    <tr key={featureKey} className={allowed ? 'bg-green-50/50' : 'bg-red-50/50'}>
                      <td className="px-3 py-2 font-mono">{featureKey}</td>
                      <td className="px-3 py-2">
                        {allowed ? (
                          <span className="text-green-700 font-medium">✓ Allowed</span>
                        ) : (
                          <span className="text-red-700 font-medium">✗ Denied</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick Links */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>🔗</span> Quick Links
            </h3>
            <div className="flex flex-wrap gap-2">
              {quickLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={onClose}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    link.access.allowed
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none'
                  }`}
                  title={link.access.allowed ? undefined : link.access.reason}
                >
                  {link.label}
                  {!link.access.allowed && ' 🚫'}
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500 text-center">
          This panel is only visible in development mode (import.meta.env.DEV)
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Badge Components
// ============================================================

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    superadmin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    expert: 'bg-green-100 text-green-800',
    user: 'bg-gray-100 text-gray-800',
    none: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[role] || colors.none}`}>
      {role}
    </span>
  );
}

function AccountTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    company: 'bg-indigo-100 text-indigo-800',
    recruiter: 'bg-orange-100 text-orange-800',
    individual: 'bg-gray-100 text-gray-800',
    none: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[type] || colors.none}`}>
      {type}
    </span>
  );
}

function AuthSourceBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    Central: 'bg-cyan-100 text-cyan-800',
    Legacy: 'bg-yellow-100 text-yellow-800',
    None: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[source] || colors.None}`}>
      {source}
    </span>
  );
}

// ============================================================
// Default Export
// ============================================================

export default DevAccessDebugPanel;
